import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useSchoolForm } from '@modules/staff/schools/hooks/useSchoolForm';
import { locationService } from '@modules/organizationalStructure/locations/services/locationService';
import { useState, useEffect } from 'react';
import Select from 'react-select';

export default function SchoolForm({ school, onClose, fetchData, selectedData }) {
    const { t } = useTranslation();
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const {
        modalRef,
        register,
        handleSubmit,
        errors,
        setValue,
        isActiveChecked,
        serverMessage,
        onSubmit
    } = useSchoolForm({
        school,
        onClose,
        selectedData,
        fetchData
    });
    

    useEffect(() => {
        const fetchCountries = async () => {
            const countriesData = await locationService.getCountries();
            const transformedCountries = countriesData.map(country => ({
                value: country.countryCode,
                label: country.countryNameBg || country.countryName || country.countryCode
            }));
            setCountries(transformedCountries);
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (school) {
            if (school.country) {
                setSelectedCountry(school.country);
                setValue('country', school.country);
            }
        }
    }, [school, setValue]);

    const handleClose = (e) => {
        e.preventDefault();
        onClose();
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog" ref={modalRef}>
                <div className="modal-content">
                    <button
                        type="button"
                        className="close"
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} size="lg" />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {school ? t('nav.schools.form.edit') : t('nav.schools.form.add')}
                        </h4>
                        <form id="schoolForm" className="region-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-grid">
                                <div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.shortName')}
                                        </span>
                                        <input
                                            type="text"
                                            placeholder={t('nav.schools.form.shortName')}
                                            className={`form-control ${errors.shortName ? 'is-invalid' : ''}`}
                                            {...register('shortName')}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.name')} <span className="required">*</span>
                                        </span>
                                        <input
                                            type="text"
                                            placeholder={t('nav.schools.form.name')}
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name.message}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.country')}
                                        </span>
                                        <Select
                                            className="form-control"
                                            options={countries}
                                            value={countries.find((country) => country.value === selectedCountry)}
                                            onChange={(selectedOption) => {
                                                setSelectedCountry(selectedOption.value);
                                                setValue('country', selectedOption.value);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.postcode')}
                                        </span>
                                        <input
                                            type="text"
                                            placeholder={t('nav.schools.form.postcode')}
                                            className={`form-control ${errors.postcode ? 'is-invalid' : ''}`}
                                            {...register('postcode')}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.city')}
                                        </span>
                                        <input
                                            type="text"
                                            placeholder={t('nav.schools.form.city')}
                                            className={`form-control ${errors.address1 ? 'is-invalid' : ''}`}
                                            {...register('address1')}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.address')}
                                        </span>
                                        <input
                                            type="text"
                                            placeholder={t('nav.schools.form.address')}
                                            className={`form-control ${errors.address2 ? 'is-invalid' : ''}`}
                                            {...register('address2')}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('nav.schools.form.isActive')}
                                        </span>
                                        <div className="input-checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                {...register('active')}
                                                checked={isActiveChecked}
                                                onChange={(e) => {
                                                    const newValue = e.target.checked;
                                                    setValue('active', newValue);
                                                }}
                                            />
                                        </div>
                                        {errors.active && (
                                            <div className="invalid-feedback">{errors.active.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {serverMessage && <div className="error-message">{serverMessage}</div>}
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="schoolForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="form-btn btn-red" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
